import { BuildingComplex } from 'lucide-react';
import googlePinIcon from 'images/google-buttons/google_pin.svg';
import closeIcon from 'images/icon/closeIcon.png';
import drag_and_drop_dots from 'images/icons/drag_and_drop_dots.svg';
import * as React from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Translate } from 'react-localize-redux';
import { areEqual, FixedSizeList } from 'react-window';
import styled from 'styled-components';
import { useWindowDimensions } from '../../../components_v2/utils';
import { PastelPrimaryColor, SidelyBlack } from '../../../styles/global/css/Utils';
import { translateToString } from '../../../styles/global/translate';
import { AlertRes } from '../../alert/AlertProvider';
import useAlert from '../../alert/UseAlert';
import RestrictedSuperAdmin from '../../permissions/RestrictedSuperAdmin';
import { FlexDiv } from '../../products/style';
import { ReactStateType } from '../Map';
import { MapContext } from '../MapContext';
import { CompaniesInLasso } from '../MapView';
import { GooglePlaceItem, InitialMapRow } from '../model';
import PageSwitch from '../PageSwitch';

export enum Panel {
	BasicList = 0,
	MassEvents = 1,
	AssignTo = 2
}

const translatePath = 'map.modal_right';

const HideScrollBar = styled.div`
  overflow-y: auto;
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  /* IE and Edge */
  scrollbar-width: none;
  /* Firefox */
  /* Hide scrollbar for Chro onSelect={setOperatorme, Safari and Opera */

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CompanyContainer = styled(FlexDiv) <{ selected: boolean }>`
  ${p => (p.selected ? `background-color: ${PastelPrimaryColor};` : '')}
  padding: 0.5em 0;
  height: 55px;
`;

const BlackDot = styled.div`
  background-color: ${SidelyBlack};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  cursor: pointer;
  color: white;
  font-size: 9px;
  font-weight: 500;
  border-radius: 50%;
`;

type ModalRightProps = {
	companiesInLasso: CompaniesInLasso;
	setCompaniesInLasso: React.Dispatch<React.SetStateAction<CompaniesInLasso>>;
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	modalRightPanelHook: ReactStateType<'sidely' | 'google'>;
};

function isInitialMapRow(
	item: InitialMapRow | GooglePlaceItem
): item is InitialMapRow {
	return 'id' in item && typeof item.id === 'number';
}

export default function ModalRight(props: ModalRightProps) {
	const { isOpen, companiesInLasso } = props;
	const [panel, setPanel] = props.modalRightPanelHook;

	React.useEffect(
		() => {
			if (isOpen) {
				setPanel(companiesInLasso.companies.length > 0 ? 'sidely' : 'google');
			}
		},
		[isOpen]
	);

	const { hoveredPin, setHoveredPin } = React.useContext(MapContext);
	const alert = useAlert();
	const items =
		panel === 'sidely'
			? companiesInLasso.companies
			: companiesInLasso.googlePlacesItems;

	const Row = React.memo(function Row(p: {
		data: (InitialMapRow | GooglePlaceItem)[];
		index: number;
		style: object;
	}) {
		const { data, index, style } = p;
		const c = data[index];
		if (!c) return null;
		return (
			<Draggable draggableId={c.id.toString()} index={index} key={c.id}>
				{provided => (
					<Item provided={provided} item={c} style={style} index={index} />
				)}
			</Draggable>
		);
	},
	areEqual);

	function Item(p: {
		provided;
		item: InitialMapRow | GooglePlaceItem;
		style?: object;
		index: number;
	}) {
		const { provided, item, style, index } = p;
		if (!item) return null;
		const [localTimeout, setLocalTimeout] = React.useState<NodeJS.Timeout>();

		const handleMouseLeave = () => {
			clearTimeout(localTimeout);
			setHoveredPin(undefined);
		};
		return (
			<div
				{...provided.draggableProps}
				{...provided.dragHandleProps}
				ref={provided.innerRef}
				style={{ ...style, ...provided.draggableProps.style }}
				onMouseEnter={() => {
					if (isInitialMapRow(item))
						return setLocalTimeout(
							setTimeout(() => setHoveredPin(item.id), 200)
						);
				}}
				onMouseLeave={handleMouseLeave}
			>
				<CompanyContainer
					justify="space-evenly"
					selected={
						typeof hoveredPin === 'object'
							? hoveredPin.some(id => id === item.id)
							: item.id === hoveredPin
					}
				>
					<img
						style={{ width: 12, cursor: 'pointer' }}
						src={closeIcon}
						onClick={() => {
							props.setCompaniesInLasso(c => {
								if (isInitialMapRow(item))
									c.companies.splice(c.companies.indexOf(item), 1);
								else
									c.googlePlacesItems.splice(
										c.googlePlacesItems.indexOf(item),
										1
									);
								return { ...c };
							});
						}}
					/>
					<div
						style={{
							display: 'inline-block',
							width: '155px',
							verticalAlign: 'middle',
							fontFamily: 'Inter',
							fontStyle: 'normal',
							fontWeight: 'normal',
							fontSize: '13px',
							lineHeight: '19px'
						}}
					>
						{isInitialMapRow(item) ? item.name : item.displayName.text}
					</div>
					<img src={drag_and_drop_dots} />
					<BlackDot>{index + 1}</BlackDot>
				</CompanyContainer>
				<hr style={{ width: '90%', margin: '0 5%' }} />
			</div>
		);
	}

	function handleOnDragEnd(result) {
		if (!result.destination) {
			return;
		}
		props.setCompaniesInLasso(c => {
			const [reorderedItem] = items.splice(result.source.index, 1);
			items.splice(result.destination.index, 0, reorderedItem);
			return { ...c };
		});
	}
	const { height } = useWindowDimensions();

	return (
		<DragDropContext onDragEnd={handleOnDragEnd}>
			<div
				className="filter-modal"
				style={{ top: 0, height: '100%', right: 0, overflow: 'visible' }}
			>
				<div
					className={isOpen ? 'box-filter-modal' : 'd-none'}
					style={{ overflow: 'visible', width: '275px' }}
				>
					{isOpen && (
						<>
							<div className="d-flex justify-content-between align-items-center px-2">
								<div
									style={{
										fontFamily: 'Inter',
										fontStyle: 'normal',
										fontWeight: 'bold',
										fontSize: '14px',
										padding: '16px 8px 16px 16px',
										color: SidelyBlack
									}}
								>
									{items.length}{' '}
									<Translate id={`${translatePath}.companies_selected`} />
								</div>
								<RestrictedSuperAdmin>
									<PageSwitch<'sidely' | 'google'>
										selectedValue={panel}
										background
										singleButton
										list={[
											{
												value: 'sidely',
												img: <BuildingComplex />,
												onClick: setPanel
											},
											{
												value: 'google',
												img: <img src={googlePinIcon} height={20} />,
												onClick: setPanel
											}
										]}
									/>
								</RestrictedSuperAdmin>
								<img
									src={closeIcon}
									className="custom-icon-3 my-auto mx-3"
									alt=""
									onClick={() => {
										alert({
											title: translateToString(
												`${translatePath}.delete_selection`
											),
											content: translateToString(
												`${translatePath}.are_you_sure_delete_selection`
											),
											buttons: [
												{
													title: translateToString(
														`${translatePath}.yes_delete_it`
													),
													res: AlertRes.Ok
												}
											],
											gap: '0px'
										}).then(res => {
											if (res.res === AlertRes.Ok) {
												props.setCompaniesInLasso({
													companies: [],
													googlePlacesItems: []
												});
											}
										});
									}}
								/>
							</div>
							<HideScrollBar
								style={{
									height: 'calc(100vh - 190px)'
								}}
							>
								<Droppable
									droppableId="companies"
									mode="virtual"
									renderClone={(provided, snapshot, rubric) => (
										<Item
											provided={provided}
											item={items[rubric.source.index]}
											index={rubric.source.index}
										/>
									)}
								>
									{provided => (
										<FixedSizeList
											height={height - 190}
											itemCount={items.length}
											itemSize={55}
											width={275}
											outerRef={provided.innerRef}
											itemData={items}
										>
											{Row}
										</FixedSizeList>
									)}
								</Droppable>
							</HideScrollBar>
						</>
					)}
				</div>
			</div>
		</DragDropContext>
	);
}
